import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type TechPartnerMapping,
  useTechPartnerProductOptions,
  useUpdateTechPartner,
} from "@/hooks/use-tech-partners";

const editSchema = z.object({
  product: z.string().min(1, "Product is required"),
  partnerName: z
    .string()
    .min(1, "Tech partner name is required")
    .max(100, "Tech partner name must be 100 characters or fewer"),
});

type EditValues = z.infer<typeof editSchema>;

interface EditTechPartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mapping: TechPartnerMapping;
  takenProducts: string[];
}

export function EditTechPartnerDialog({
  open,
  onOpenChange,
  mapping,
  takenProducts,
}: EditTechPartnerDialogProps) {
  const { data: productOptions = [], isLoading: isLoadingProducts } =
    useTechPartnerProductOptions();
  const update = useUpdateTechPartner();

  const takenSet = new Set(takenProducts);
  const availableProducts = productOptions.filter(
    (product) => product === mapping.product || !takenSet.has(product),
  );

  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { product: mapping.product, partnerName: mapping.partnerName },
  });

  useEffect(() => {
    if (open) {
      form.reset({ product: mapping.product, partnerName: mapping.partnerName });
    }
  }, [open, mapping, form]);

  function onSubmit(values: EditValues) {
    update.mutate(
      { id: mapping.id, ...values },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Pairing</DialogTitle>
          <DialogDescription>Update this product/tech partner pairing.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="product"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={isLoadingProducts ? "Loading products…" : "Select a product"}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableProducts.map((product) => (
                        <SelectItem key={product} value={product}>
                          {product}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="partnerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tech Partner</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Jordan Lee" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
